/*
 * ============================================
 * 🔧 CUSTOM CODE - DO NOT REMOVE
 * ============================================
 * Feature: Echo-ketting configurator (interne naam echohanger, v3)
 * Hoort bij: snippets/custom-echohanger.liquid, assets/custom-echohanger.css, blok 'echohanger' in
 *            sections/main-product.liquid, templates/product.echohanger.json
 *            engine (niet wijzigen): assets/echohanger-v1-beeld.js
 *            contract: echohanger/docs/SPEC.md in de repo blf-gegraveerde-hanger (§ 7, 8 en 14)
 * Datum: 2026-10-14 (v2: echo-ketting); 2026-11-03 (v3, Frank): de klant doet alleen een upload en kiest een
 *        kleur. Geen uitsnede, geen achterkant, en "In winkelwagen" uploadt de echo zelf. Twee vinkjes.
 *
 * Web Component <echohanger-configurator>:
 * - laadt de beeld-engine pas als de configurator bijna in beeld komt (of bij de eerste klik);
 * - de klant kiest een echo; de browser verkleint hem (lange zijde hoogstens 2000 px), maakt hem grijs
 *   (EchohangerBeeld.naarGrijs/naarGrijs8) en toont een miniatuur van precies wat er naar ons gaat. Waarschuwingen
 *   (EchohangerBeeld.kwaliteit op de cirkel in het midden; alleen 'te_klein' blokkeert). De dienst wordt bij de
 *   eerste gekozen echo gewekt (GET /healthz);
 * - de kleur (Goud/Zilver) kiest de variant, zoals de Letterslinger: deze code zet input[name="id"] en de prijs
 *   (template: hide_variants, geen variant_picker). Heeft het product nog een optie Achterkant, dan telt alleen 'Nee';
 * - twee verplichte vinkjes: toestemming (apart, art. 7 lid 2 AVG) en voorwaarden;
 * - "In winkelwagen" (capture-fase van submit op <product-info>, die is de baas): ontbreekt er iets, dan een
 *   melding en de focus op wat ontbreekt; anders eerst de upload (PNG via CompressionStream('deflate') naar
 *   POST {dienst}/api/upload, schema 3), dan de properties, en dan het formulier opnieuw versturen zodat Dawn de
 *   ketting in de winkelwagen legt. Een upload geldt alleen voor precies deze echo, kleur en vinkjes; na het
 *   toevoegen is de volgende ketting een nieuwe upload;
 * - knoppen voor direct afrekenen worden tegengehouden tot de ketting via "In winkelwagen" gaat;
 * - thema-editor (Shopify.designMode): In winkelwagen is een proef zonder netwerk.
 * ============================================
 */
if (!customElements.get('echohanger-configurator')) {
  /* Versie van deze UI in het manifest (versies.configurator, max. 40 tekens) */
  const ECHOHANGER_VERSIE = 'custom-echohanger ketting 2026-11-03';
  /* De echo die naar ons gaat: lange zijde hoogstens 2000 px (de dienst weigert meer; korte zijde minstens 250) */
  const ECHOHANGER_MAX_ZIJDE = 2000;
  /* Miniatuur: zo breed (in CSS-pixels hoogstens 32rem, zie de CSS); de achtergrondmaat volgt de schermdichtheid */
  const ECHOHANGER_MINIATUUR = 640;
  const ECHOHANGER_ID = /^eh-[a-z0-9]{20}$/;
  const ECHOHANGER_HASH = /^[0-9a-f]{64}$/i;
  const ECHOHANGER_KWALITEIT = ['te_klein', 'klein', 'kleur', 'glans', 'flets'];
  /* Optiewaarden van de varianten (ook vertaald) -> kleur en achterkant; anders telt de volgorde (Goud, Zilver /
     Nee, Ja). Vergeleken in kleine letters zonder accenten; een waarde mag ook beginnen met zo'n woord. */
  const ECHOHANGER_WAARDEN = {
    goud: ['goud', 'gold', 'golden', 'goldfarben', 'goudkleurig', 'or', 'oro', 'dore', 'doree', 'dorado', 'dourado'],
    zilver: ['zilver', 'silver', 'silber', 'silberfarben', 'zilverkleurig', 'argent', 'argente', 'argentee', 'argento', 'plata', 'plateado', 'prata', 'prateado'],
    ja: ['ja', 'yes', 'oui', 'si', 'sim', 'met tekst', 'with text', 'mit text', 'avec texte', 'con texto'],
    nee: ['nee', 'no', 'nein', 'non', 'nao', 'zonder tekst', 'without text', 'ohne text', 'sans texte', 'sin texto'],
  };
  /* Gedeeld tussen alle exemplaren: na een herrender in de thema-editor niets opnieuw laden */
  const echohangerLaden = { scripts: {}, motor: null, gewekt: {} };

  /* Na de volgende schildering (zodat een "bezig"-melding eerst zichtbaar is) */
  const echohangerNaSchilderen = () =>
    new Promise((resolve) => {
      if (typeof requestAnimationFrame !== 'function' || document.hidden) {
        setTimeout(resolve, 0);
        return;
      }
      requestAnimationFrame(() => setTimeout(resolve, 0));
    });
  const echohangerWaarde = (waarde) =>
    String(waarde == null ? '' : waarde)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  const echohangerPast = (waarde, namen) => {
    const w = echohangerWaarde(waarde);
    return namen.some((naam) => w === naam || (w.indexOf(naam) === 0 && !/[a-z0-9]/.test(w.charAt(naam.length))));
  };

  /* Eén script één keer laden */
  const echohangerScript = (src, isGeladen) => {
    if (isGeladen()) return Promise.resolve();
    if (!echohangerLaden.scripts[src]) {
      echohangerLaden.scripts[src] = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = false;
        script.onload = () => (isGeladen() ? resolve() : reject(new Error(`[echohanger] ${src} gaf geen resultaat`)));
        script.onerror = () => reject(new Error(`[echohanger] ${src} kon niet laden`));
        document.head.appendChild(script);
      }).catch((error) => {
        delete echohangerLaden.scripts[src];
        throw error;
      });
    }
    return echohangerLaden.scripts[src];
  };

  /* De beeld-engine; geeft EchohangerBeeld, gedeeld door alle exemplaren */
  const echohangerLaadMotor = (bron) => {
    if (!echohangerLaden.motor) {
      echohangerLaden.motor = echohangerScript(bron, () => Boolean(window.EchohangerBeeld && window.EchohangerBeeld.naarGrijs8))
        .then(() => window.EchohangerBeeld)
        .catch((error) => {
          echohangerLaden.motor = null;
          throw error;
        });
    }
    return echohangerLaden.motor;
  };

  const echohangerMetTijd = (url, opties, ms) => {
    const regelaar = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = regelaar ? setTimeout(() => regelaar.abort(), ms) : null;
    return fetch(url, regelaar ? { ...opties, signal: regelaar.signal } : opties).finally(() => clearTimeout(timer));
  };

  /* Wekt de dienst (die slaapt na een stille periode); /healthz heeft geen CORS nodig: no-cors */
  const echohangerWek = (dienst) => {
    if (!dienst || echohangerLaden.gewekt[dienst]) return;
    echohangerLaden.gewekt[dienst] = true;
    fetch(`${dienst}/healthz`, { mode: 'no-cors', credentials: 'omit', cache: 'no-store' }).catch(() => {});
  };

  /* Foto -> canvas van hoogstens 2000 px (EXIF-stand toegepast; de EXIF zelf gaat niet mee: alleen pixels);
     createImageBitmap, anders een <img> */
  const echohangerNaarCanvas = async (bestand) => {
    let bron = null;
    let url = null;
    if (typeof createImageBitmap === 'function') {
      try {
        bron = await createImageBitmap(bestand, { imageOrientation: 'from-image' });
      } catch (error) {
        try {
          bron = await createImageBitmap(bestand);
        } catch (error2) {
          bron = null;
        }
      }
    }
    if (!bron) {
      url = URL.createObjectURL(bestand);
      bron = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('[echohanger] foto niet te lezen'));
        img.src = url;
      });
    }
    try {
      const bw = bron.naturalWidth || bron.width;
      const bh = bron.naturalHeight || bron.height;
      if (!(bw > 0 && bh > 0)) throw new Error('[echohanger] foto zonder maat');
      const schaal = Math.min(1, ECHOHANGER_MAX_ZIJDE / Math.max(bw, bh));
      const w = Math.max(1, Math.min(ECHOHANGER_MAX_ZIJDE, Math.round(bw * schaal)));
      const h = Math.max(1, Math.min(ECHOHANGER_MAX_ZIJDE, Math.round(bh * schaal)));
      let teken = bron;
      if (schaal < 1 && typeof ImageBitmap !== 'undefined' && bron instanceof ImageBitmap) {
        try {
          const klein = await createImageBitmap(bron, { resizeWidth: w, resizeHeight: h, resizeQuality: 'high' });
          if (klein.width === w && klein.height === h) teken = klein;
          else klein.close();
        } catch (error) {
          /* tekenen op het canvas schaalt ook */
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(teken, 0, 0, w, h);
      if (teken !== bron && teken.close) teken.close();
      return { canvas, ctx, w, h };
    } finally {
      if (bron && bron.close) bron.close();
      if (url) URL.revokeObjectURL(url);
    }
  };

  /* Grijs 8 bit -> de miniatuur: precies de pixels die naar de dienst gaan, verkleind tot de breedte van het doek */
  const echohangerTekenMiniatuur = (data, w, h, doek, breedte) => {
    const volledig = document.createElement('canvas');
    volledig.width = w;
    volledig.height = h;
    const vctx = volledig.getContext('2d');
    const beeld = vctx.createImageData(w, h);
    const d = beeld.data;
    for (let i = 0, k = 0; i < data.length; i += 1, k += 4) {
      d[k] = data[i];
      d[k + 1] = data[i];
      d[k + 2] = data[i];
      d[k + 3] = 255;
    }
    vctx.putImageData(beeld, 0, 0);
    const dw = Math.max(1, Math.min(w, Math.round(breedte)));
    const dh = Math.max(1, Math.round((h * dw) / w));
    doek.width = dw;
    doek.height = dh;
    const ctx = doek.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(volledig, 0, 0, dw, dh);
    volledig.width = 0;
  };

  /* PNG-IDAT: zlib (CompressionStream 'deflate', niet 'deflate-raw'); zonder CompressionStream zlib met
     ongecomprimeerde blokken (geldig, alleen groter; 2000 x 2000 past nog binnen de 8 MB van de dienst) */
  const echohangerZlibOpgeslagen = (bytes) => {
    const n = bytes.length;
    const blokken = Math.max(1, Math.ceil(n / 65535));
    const uit = new Uint8Array(2 + n + blokken * 5 + 4);
    let p = 0;
    uit[p++] = 0x78;
    uit[p++] = 0x01;
    for (let b = 0; b < blokken; b++) {
      const van = b * 65535;
      const lengte = Math.min(65535, n - van);
      uit[p++] = b === blokken - 1 ? 1 : 0;
      uit[p++] = lengte & 255;
      uit[p++] = (lengte >>> 8) & 255;
      uit[p++] = ~lengte & 255;
      uit[p++] = (~lengte >>> 8) & 255;
      uit.set(bytes.subarray(van, van + lengte), p);
      p += lengte;
    }
    let a = 1;
    let s = 0;
    for (let i = 0; i < n; i++) {
      a = (a + bytes[i]) % 65521;
      s = (s + a) % 65521;
    }
    uit[p++] = (s >>> 8) & 255;
    uit[p++] = s & 255;
    uit[p++] = (a >>> 8) & 255;
    uit[p++] = a & 255;
    return uit;
  };
  const echohangerDeflate = (bytes) => {
    if (typeof CompressionStream === 'function') {
      const stroom = new Blob([bytes]).stream().pipeThrough(new CompressionStream('deflate'));
      return new Response(stroom).arrayBuffer().then((buffer) => new Uint8Array(buffer));
    }
    return Promise.resolve(echohangerZlibOpgeslagen(bytes));
  };

  const echohangerBase64 = (bytes) => {
    let tekst = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
      tekst += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    }
    return btoa(tekst);
  };

  const echohangerSha256 = async (tekst) => {
    const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(tekst));
    return Array.from(new Uint8Array(buffer), (b) => b.toString(16).padStart(2, '0')).join('');
  };

  /* Platte tekst van een vinkje zoals de klant hem ziet (zonder HTML, witruimte samengevoegd): van deze tekst gaat
     de vingerafdruk (SHA-256) mee. Register van de vingerafdrukken: echohanger/docs/README.md in de repo blf-gegraveerde-hanger */
  const echohangerPlat = (el) => (el ? el.textContent : '').replace(/\s+/g, ' ').trim();

  customElements.define(
    'echohanger-configurator',
    class EchohangerConfigurator extends HTMLElement {
      constructor() {
        super();
        this.motor = null; // Promise van EchohangerBeeld
        this.B = null; // EchohangerBeeld
        this.klaar = false; // engine geladen
        this.laadFout = false;
        this.bron = null; // { nr, w, h, data (grijs 8 bit), kleur, kwaliteit, naam }
        this.bronNr = 0;
        this.kwaliteit = null;
        this.kwaliteitSleutel = '';
        this.kleur = 'goud';
        this.varianten = [];
        this.variant = null;
        this.upload = null; // { id, hash, tijd, toestemmingHash, voorwaardenHash, variantId, sleutel }: de upload voor deze keuzes
        this.bezig = false; // uploaden loopt
        this.productFormErrorShown = false;
        this.onSubmit = this.onSubmit.bind(this);
        this.onPaymentButtonClick = this.onPaymentButtonClick.bind(this);
        this.onVerlaten = this.onVerlaten.bind(this);
        this.start = this.start.bind(this);
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.form = this.resolveForm();
        /* getAttribute: het formulier bevat een input name="id", waardoor form.id dat element teruggeeft */
        this.formId = this.form ? this.form.getAttribute('id') : this.dataset.formId;
        this.productForm = this.form ? this.form.closest('product-form') : null;
        this.priceContainer = document.getElementById(`price-${this.dataset.sectionId}`);
        this.dienst = (this.dataset.dienst || '').trim().replace(/\/+$/, '');

        if (!this.gekoppeld) {
          if (!this.zoekElementen()) return;
          this.strings = this.readStrings();
          this.leesProducten();
          this.kiesStartKleur();
          this.koppel();
          /* Links in de teksten (zoals /pages/contact) in een nieuw tabblad: weggaan zou de keuzes kwijtraken */
          this.querySelectorAll('.echohanger__akkoord-tekst a[href], .echohanger__privacy a[href], .echohanger__uitleg a[href]').forEach((link) => {
            if (!link.target) link.target = '_blank';
            link.rel = 'noopener';
          });
          this.gekoppeld = true;
        } else if (!this.bron) {
          /* opnieuw in de pagina gezet (de echo is bij het weghalen vrijgegeven) */
          if (this.kiesTekst) this.kiesTekst.textContent = this.text('choose_file');
          if (this.bestandInfo) this.bestandInfo.textContent = this.text('file_types', { max: Number(this.dataset.maxMb) || 30 });
          if (this.miniatuur) this.miniatuur.hidden = true;
          this.toonKwaliteit(null);
        }
        this.koppelBuiten();
        this.kiesVariant();
        this.werkBedieningBij();

        /* Laden pas als de configurator bijna in beeld is, of bij de eerste bediening */
        if (this.motor) return;
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) this.start();
            },
            { rootMargin: '400px 0px' }
          );
          this.observer.observe(this);
        } else {
          this.start();
        }
      }

      zoekElementen() {
        const $ = (selector) => this.querySelector(selector);
        const $$ = (selector) => Array.from(this.querySelectorAll(selector));
        this.bestandInput = $('[data-echohanger-bestand]');
        this.kiesKnop = $('[data-echohanger-kies]');
        this.kiesTekst = $('[data-echohanger-kies-tekst]');
        this.bestandInfo = $('[data-echohanger-bestand-info]');
        this.bestandFout = $('[data-echohanger-bestand-fout]');
        this.sleepzone = $('[data-echohanger-sleepzone]');
        this.miniatuur = $('[data-echohanger-miniatuur]');
        this.miniatuurDoek = $('[data-echohanger-miniatuur-doek]');
        this.kwaliteitEl = $('[data-echohanger-kwaliteit]');
        this.kleurInputs = $$('[data-echohanger-kleur]');
        this.variantFout = $('[data-echohanger-variant-fout]');
        this.toestemmingInput = $('[data-echohanger-toestemming]');
        this.toestemmingTekstEl = $('[data-echohanger-toestemming-tekst]');
        this.voorwaardenInput = $('[data-echohanger-voorwaarden]');
        this.voorwaardenTekstEl = $('[data-echohanger-voorwaarden-tekst]');
        this.bestellenSectie = $('[data-echohanger-bestellen]');
        this.statusEl = $('[data-echohanger-status]');
        this.errorElement = $('[data-echohanger-fout]');
        this.foutcodeEl = $('[data-echohanger-foutcode]');
        this.opnieuwKnop = $('[data-echohanger-opnieuw]');
        this.toegevoegdEl = $('[data-echohanger-toegevoegd]');
        this.propertiesContainer = $('[data-echohanger-properties]');
        return Boolean(this.bestandInput && this.propertiesContainer && this.toestemmingInput && this.voorwaardenInput && this.miniatuurDoek);
      }

      disconnectedCallback() {
        clearTimeout(this.traagTimer);
        window.removeEventListener('beforeunload', this.onVerlaten);
        if (this.observer) this.observer.disconnect();
        this.observer = null;
        (this.productInfo || this.form)?.removeEventListener('submit', this.onSubmit, true);
        (this.productInfo || this.form)?.removeEventListener('click', this.onPaymentButtonClick, true);
        if (typeof this.cartUpdateUnsubscriber === 'function') this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = null;
        /* Echo vrijgeven; bij opnieuw invoegen begint de klant opnieuw */
        this.motor = null;
        this.bron = null;
        this.upload = null;
        if (this.miniatuurDoek) this.miniatuurDoek.width = 0;
        if (this.propertiesContainer) this.propertiesContainer.innerHTML = '';
      }

      /* ---------- initialisatie ---------- */

      resolveForm() {
        const byId = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
        if (byId && (!this.productInfo || this.productInfo.contains(byId))) return byId;
        return this.productInfo ? this.productInfo.querySelector('form[data-type="add-to-cart-form"]') : byId;
      }

      /* HTML-entiteiten terug naar tekens (de t-filter en money kunnen &amp; of &euro; geven); er worden geen
         elementen gemaakt: een textarea zet alleen entiteiten om */
      los(waarde) {
        if (typeof waarde !== 'string' || waarde.indexOf('&') < 0) return waarde;
        this.ontsleutel = this.ontsleutel || document.createElement('textarea');
        this.ontsleutel.innerHTML = waarde;
        return this.ontsleutel.value;
      }

      /* Vertaalde teksten uit de snippet (locales echohanger.*); [naam] wordt hier ingevuld */
      readStrings() {
        const script = this.querySelector('[data-echohanger-strings]');
        if (!script) return {};
        try {
          const ruw = JSON.parse(script.textContent);
          Object.keys(ruw).forEach((key) => {
            ruw[key] = this.los(ruw[key]);
          });
          return ruw;
        } catch (error) {
          console.error('[echohanger] Teksten konden niet gelezen worden', error);
          return {};
        }
      }

      text(key, values = {}) {
        return Object.entries(values).reduce(
          (result, [name, value]) => result.split(`[${name}]`).join(String(value)),
          this.strings[key] || ''
        );
      }

      /* Varianten (zoals de Letterslinger), met per variant de kleur ('goud'/'zilver') uit de optiewaarden. Welke optie
         de kleur is: aan de waarden (ook vertaald), anders optie 1. Heeft het product nog een optie Achterkant (van
         vóór 3-11-2026: Nee/Ja), dan doen alleen de varianten met 'Nee' mee: tekst op de achterkant kan niet meer. */
      leesProducten() {
        let data = {};
        const script = this.querySelector('[data-echohanger-producten]');
        try {
          data = script ? JSON.parse(script.textContent) : {};
        } catch (error) {
          console.error('[echohanger] Varianten konden niet gelezen worden', error);
        }
        const opties = Array.isArray(data.opties) ? data.opties : [];
        const herkend = (waarde, soorten) => soorten.find((soort) => echohangerPast(waarde, ECHOHANGER_WAARDEN[soort])) || null;
        const vindOptie = (soorten, standaard, behalve) => {
          const i = opties.findIndex((optie, index) => index !== behalve && (optie.waarden || []).some((w) => herkend(w, soorten)));
          if (i >= 0) return i;
          return standaard >= 0 && standaard < opties.length && standaard !== behalve ? standaard : -1;
        };
        /* Zonder herkende kleurwaarden telt optie 1 alleen als kleur als hij minstens twee waarden heeft (een product
           met alleen 'Default Title' is niet goed ingesteld) */
        let kleurOptie = vindOptie(['goud', 'zilver'], 0, -1);
        if (kleurOptie >= 0 && !(opties[kleurOptie].waarden || []).some((w) => herkend(w, ['goud', 'zilver'])) && (opties[kleurOptie].waarden || []).length < 2) kleurOptie = -1;
        const achterOptie = vindOptie(['nee', 'ja'], -1, kleurOptie);
        const vertaal = (optie, waarde, soorten) => {
          const opNaam = herkend(waarde, soorten);
          if (opNaam) return opNaam;
          const waarden = (opties[optie] && opties[optie].waarden) || [];
          if (waarden.some((w) => herkend(w, soorten))) return null;
          const i = waarden.indexOf(waarde);
          return i >= 0 && i < soorten.length ? soorten[i] : null;
        };
        /* alle varianten (ook met 'Ja', voor de startkleur), en de bestelbare: met een kleur en zonder achterkant */
        this.alleVarianten = (Array.isArray(data.varianten) ? data.varianten : []).map((v) => ({
          id: v.id,
          kleur: kleurOptie >= 0 ? vertaal(kleurOptie, v[`option${kleurOptie + 1}`], ['goud', 'zilver']) : null,
          achterkant: achterOptie >= 0 ? vertaal(achterOptie, v[`option${achterOptie + 1}`], ['nee', 'ja']) : 'nee',
          available: Boolean(v.available),
          price: this.los(String(v.price || '')),
          compare_at_price: v.compare_at_price ? this.los(String(v.compare_at_price)) : '',
        }));
        this.varianten = this.alleVarianten.filter((v) => v.id && v.kleur && v.achterkant === 'nee');
      }

      /* Startkleur: die van de gekozen variant (?variant=, ook een oude met achterkant), anders goud */
      kiesStartKleur() {
        const start = (this.alleVarianten || []).find((v) => v.kleur && String(v.id) === String(this.dataset.variantStart));
        const kleuren = this.varianten.map((v) => v.kleur);
        this.kleur = start ? start.kleur : kleuren.indexOf('goud') >= 0 || !kleuren.length ? 'goud' : kleuren[0];
        this.kleurInputs.forEach((input) => {
          input.checked = input.value === this.kleur;
        });
      }

      koppel() {
        this.kiesKnop?.addEventListener('click', () => {
          this.start();
          this.bestandInput.click();
        });
        this.bestandInput.addEventListener('change', () => {
          const bestand = this.bestandInput.files && this.bestandInput.files[0];
          this.bestandInput.value = '';
          if (bestand) this.kiesBestand(bestand);
        });
        /* Foto op stap 1 slepen (computer) */
        this.sleepzone?.addEventListener('dragover', (event) => {
          if (!event.dataTransfer || Array.from(event.dataTransfer.types || []).indexOf('Files') < 0 || this.bezig) return;
          event.preventDefault();
          this.sleepzone.classList.add('is-sleepdoel');
        });
        this.sleepzone?.addEventListener('dragleave', () => this.sleepzone.classList.remove('is-sleepdoel'));
        this.sleepzone?.addEventListener('drop', (event) => {
          this.sleepzone.classList.remove('is-sleepdoel');
          const bestand = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
          if (!bestand || this.bezig) return;
          event.preventDefault();
          this.kiesBestand(bestand);
        });

        this.kleurInputs.forEach((input) =>
          input.addEventListener('change', () => {
            if (!input.checked) return;
            this.kleur = input.value;
            this.kiesVariant();
            this.naWijziging();
          })
        );
        this.toestemmingInput.addEventListener('change', () => this.naWijziging());
        this.voorwaardenInput.addEventListener('change', () => this.naWijziging());
        this.opnieuwKnop?.addEventListener('click', () => this.bestel());

        /* Eerste bediening start het laden ook (als de IntersectionObserver nog niet zover is) */
        this.addEventListener('pointerdown', this.start);
        this.addEventListener('focusin', this.start);
        /* Thema-editor: blok aanklikken = laden */
        this.addEventListener('shopify:block:select', this.start);
      }

      /* Luisteraars buiten het element: bij elke connect opnieuw, bij disconnect weg */
      koppelBuiten() {
        /* Capture-fase op <product-info>: deze controle is de baas (Dawn zet de knop soms zelf weer aan) */
        (this.productInfo || this.form)?.addEventListener('submit', this.onSubmit, true);
        /* "Direct kopen"-knoppen omzeilen het submit-event; ook die tegenhouden */
        (this.productInfo || this.form)?.addEventListener('click', this.onPaymentButtonClick, true);
        if (!this.cartUpdateUnsubscriber && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
        }
      }

      /* De beeld-engine laden */
      start() {
        if (this.motor || !this.isConnected) return this.motor;
        if (this.laadFout && Date.now() - this.laadFoutTijd < 5000) return null; // niet bij elke klik opnieuw
        if (this.observer) this.observer.disconnect();
        this.observer = null;
        this.laadFout = false;
        this.motor = echohangerLaadMotor(this.dataset.srcBeeld);
        this.motor
          .then((B) => {
            if (!this.isConnected) return;
            this.B = B;
            this.klaar = true;
            this.dataset.versies = B.VERSIE;
            this.werkBedieningBij();
          })
          .catch((error) => {
            console.error('[echohanger] Laden mislukt', error);
            if (!this.isConnected) return;
            this.laadFout = true;
            this.laadFoutTijd = Date.now();
            this.motor = null; // volgende bediening probeert opnieuw
            this.toonBestandFout(this.text('load_error'));
            this.werkBedieningBij();
          });
        return this.motor;
      }

      isEditor() {
        return Boolean(window.Shopify && window.Shopify.designMode);
      }

      taal() {
        return String((window.Shopify && window.Shopify.locale) || document.documentElement.lang || '').slice(0, 10);
      }

      kleurNaam() {
        return this.text(`color_${this.kleur}`) || this.kleur;
      }

      knop() {
        return this.form ? this.form.querySelector('[type="submit"]') : null;
      }

      /* ---------- varianten, prijs en de knop In winkelwagen ---------- */

      kiesVariant() {
        const variant = this.varianten.find((v) => v.kleur === this.kleur) || null;
        this.variant = variant;
        this.setVariantId(variant ? variant.id : '');
        if (variant) this.renderPrice(variant);
        this.dataset.variant = variant ? String(variant.id) : '';
        this.werkVariantMeldingBij();
        this.werkWinkelknopBij();
      }

      setVariantId(id) {
        const forms = [this.form, document.getElementById(`product-form-installment-${this.dataset.sectionId}`)];
        forms.forEach((form) => {
          const input = form ? form.querySelector('input[name="id"]') : null;
          if (!input || String(input.value) === String(id)) return;
          input.value = id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      /* Prijs in hetzelfde DOM als de Letterslinger (snippets/price.liquid in #price-{sectie}) */
      renderPrice(variant) {
        if (!this.priceContainer || !variant) return;
        const price = this.priceContainer.querySelector('.price');
        const regular = this.priceContainer.querySelector('.price__regular .price-item--regular');
        const sale = this.priceContainer.querySelector('.price__sale .price-item--sale');
        const compareAt = this.priceContainer.querySelector('.price__sale s.price-item--regular');
        if (regular) regular.textContent = variant.price;
        if (sale) sale.textContent = variant.price;
        if (compareAt) compareAt.textContent = variant.compare_at_price || '';
        if (price) {
          price.classList.toggle('price--on-sale', Boolean(variant.compare_at_price));
          price.classList.toggle('price--sold-out', !variant.available);
        }
      }

      /* Ontbrekende of uitverkochte kleur: duidelijke melding, niet bestelbaar */
      variantMelding() {
        if (!this.varianten.length) return this.text('error_no_variants');
        if (!this.variant) return this.text('variant_unavailable', { choice: this.kleurNaam() });
        if (!this.variant.available) return this.text('variant_sold_out', { choice: this.kleurNaam() });
        return '';
      }

      werkVariantMeldingBij() {
        if (!this.variantFout) return;
        const melding = this.variantMelding();
        if (this.variantFout.textContent !== melding) this.variantFout.textContent = melding;
        this.variantFout.hidden = !melding;
      }

      /* De knop In winkelwagen: aan met een bestelbare variant (ook zonder echo of vinkje: een klik zegt dan wat er
         ontbreekt). Deze configurator kiest de variant zelf (geen variant_picker), dus hij is ook de baas over
         uitverkocht / niet beschikbaar. Tijdens het uploaden: aria-disabled, de draaier van Dawn naast een zichtbare
         tekst ("Even geduld…"; de CSS houdt de tekst zichtbaar via data-echohanger-bezig). */
      werkWinkelknopBij() {
        const knop = this.knop();
        if (!knop) return;
        const tekst = knop.querySelector('span');
        if (tekst && !knop.dataset.echohangerTekst) knop.dataset.echohangerTekst = tekst.textContent.trim();
        const strings = window.variantStrings || {};
        let label;
        if (!this.variant) label = strings.unavailable || this.text('unavailable');
        else if (!this.variant.available) label = strings.soldOut || this.text('sold_out');
        else if (this.bezig) label = this.text('uploading_button');
        else label = strings.addToCart || knop.dataset.echohangerTekst;
        const aan = Boolean(this.variant && this.variant.available);
        if (knop.disabled !== !aan) knop.disabled = !aan;
        if (tekst && label && tekst.textContent.trim() !== label) tekst.textContent = label;
        const draaier = knop.querySelector('.loading__spinner');
        if (this.bezig) {
          knop.setAttribute('aria-disabled', 'true');
          knop.setAttribute('aria-busy', 'true');
          knop.classList.add('loading');
          draaier?.classList.remove('hidden');
          knop.dataset.echohangerBezig = '';
        } else if (knop.hasAttribute('data-echohanger-bezig')) {
          /* alleen terugzetten wat deze configurator zelf aanzette (niet de draaier van Dawn bij het toevoegen) */
          knop.removeAttribute('data-echohanger-bezig');
          knop.removeAttribute('aria-disabled');
          knop.removeAttribute('aria-busy');
          knop.classList.remove('loading');
          draaier?.classList.add('hidden');
        }
      }

      /* ---------- de echo ---------- */

      async kiesBestand(bestand) {
        if (this.bezig) return;
        this.verbergBestandFout();
        this.verbergToegevoegd();
        this.hideError();
        const maxMb = Number(this.dataset.maxMb) || 30;
        const type = String(bestand.type || '').toLowerCase();
        if (!/^image\/(jpeg|png|webp)$/.test(type) && !(type === '' && /\.(jpe?g|png|webp)$/i.test(bestand.name || ''))) {
          this.toonBestandFout(this.text('file_error_type'));
          return;
        }
        if (bestand.size > maxMb * 1024 * 1024) {
          this.toonBestandFout(this.text('file_error_size', { max: maxMb }));
          return;
        }
        /* Eerste echo: nu pas de dienst wekken (/healthz); thema-editor: geen netwerk */
        if (!this.isEditor()) echohangerWek(this.dienst);
        const nr = ++this.bronNr;
        if (this.bestandInfo) this.bestandInfo.textContent = this.text('file_reading');
        try {
          const motor = this.start();
          const geladen = await echohangerNaarCanvas(bestand);
          if (!motor) throw new Error('[echohanger] engine niet geladen');
          const B = await motor;
          if (nr !== this.bronNr || !this.isConnected) return;
          await echohangerNaSchilderen();
          const rgba = geladen.ctx.getImageData(0, 0, geladen.w, geladen.h).data;
          geladen.canvas.width = 0; // geheugen terug
          const beeld = B.naarGrijs(rgba, geladen.w, geladen.h);
          /* naarGrijs zet beeld.kleur (isKleur): een gekleurde (3D/sepia) echo krijgt de tip "alleen 2D" */
          const kleur = typeof beeld.kleur === 'boolean' ? beeld.kleur : B.isKleur(rgba, geladen.w, geladen.h);
          let kwaliteit = null;
          try {
            /* Kwaliteit op de cirkel in het midden die de korte zijde vult: bronPx = de korte zijde */
            kwaliteit = B.kwaliteit(beeld, { cx: geladen.w / 2, cy: geladen.h / 2, s: Math.min(geladen.w, geladen.h) / B.MASTER, hoek: 0, kleur: Boolean(kleur) });
          } catch (error) {
            console.warn('[echohanger] Kwaliteit niet te bepalen', error);
          }
          const data = B.naarGrijs8(beeld);
          this.bron = { nr, w: geladen.w, h: geladen.h, data, kleur: Boolean(kleur), naam: bestand.name || '' };
          this.dataset.echo = `${geladen.w}x${geladen.h}`;
          this.kwaliteitSleutel = null; // lijst zeker opnieuw opbouwen
          this.toonKwaliteit(kwaliteit || { waarschuwingen: [] });
          this.tekenMiniatuur();
          if (this.bestandInfo) this.bestandInfo.textContent = this.text('file_chosen', { name: this.bron.naam || '…' });
          if (this.kiesTekst) this.kiesTekst.textContent = this.text('change_file');
          this.naWijziging();
        } catch (error) {
          console.error('[echohanger] Foto lezen mislukt', error);
          if (nr !== this.bronNr || !this.isConnected) return;
          if (this.bestandInfo) {
            this.bestandInfo.textContent = this.bron ? this.text('file_chosen', { name: this.bron.naam || '…' }) : this.text('file_types', { max: maxMb });
          }
          this.toonBestandFout(this.text(this.laadFout ? 'load_error' : 'file_error_read'));
        }
      }

      tekenMiniatuur() {
        if (!this.bron || !this.miniatuurDoek) return;
        this.miniatuur.hidden = false;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const css = this.miniatuur.getBoundingClientRect().width || ECHOHANGER_MINIATUUR / 2;
        echohangerTekenMiniatuur(this.bron.data, this.bron.w, this.bron.h, this.miniatuurDoek, Math.min(ECHOHANGER_MINIATUUR, css * dpr));
      }

      /* Waarschuwingen in vaste volgorde; 'kleur' (3D-echo) komt van isKleur op de hele foto */
      toonKwaliteit(kwaliteit) {
        const lijst = ((kwaliteit && kwaliteit.waarschuwingen) || []).filter((w) => ECHOHANGER_KWALITEIT.indexOf(w) >= 0);
        if (kwaliteit && this.bron && this.bron.kleur && lijst.indexOf('kleur') < 0) lijst.push('kleur');
        lijst.sort((x, y) => ECHOHANGER_KWALITEIT.indexOf(x) - ECHOHANGER_KWALITEIT.indexOf(y));
        const sleutel = lijst.filter((w, i) => lijst.indexOf(w) === i).join(',');
        this.kwaliteit = kwaliteit ? { ...kwaliteit, waarschuwingen: sleutel ? sleutel.split(',') : [] } : null;
        this.dataset.kwaliteit = sleutel;
        if (!this.kwaliteitEl || sleutel === this.kwaliteitSleutel) return;
        this.kwaliteitSleutel = sleutel;
        this.kwaliteitEl.innerHTML = '';
        (sleutel ? sleutel.split(',') : []).forEach((waarschuwing) => {
          const item = document.createElement('li');
          item.className = `echohanger__waarschuwing${waarschuwing === 'te_klein' ? ' is-blokkerend' : ''}`;
          item.textContent = this.text(`quality_${waarschuwing}`);
          this.kwaliteitEl.appendChild(item);
        });
        this.kwaliteitEl.hidden = !sleutel;
      }

      teKlein() {
        return Boolean(this.kwaliteit && (this.kwaliteit.waarschuwingen || []).indexOf('te_klein') >= 0);
      }

      /* ---------- de upload ---------- */

      /* Handtekening van alles wat de klant koos; een upload geldt alleen voor precies deze handtekening */
      sleutel() {
        if (!this.bron) return null;
        return JSON.stringify([
          this.bron.nr,
          this.kleur,
          Boolean(this.toestemmingInput.checked),
          Boolean(this.voorwaardenInput.checked),
          this.variant ? String(this.variant.id) : null,
        ]);
      }

      uploadGeldig() {
        return Boolean(this.upload) && this.upload.sleutel === this.sleutel();
      }

      /* Na elke wijziging: oude meldingen weg; de upload vervalt als de keuzes niet meer dezelfde zijn (de volgende
         klik op In winkelwagen uploadt opnieuw) */
      naWijziging() {
        this.verbergToegevoegd();
        this.hideError();
        this.zetStatus('');
        if (this.upload && !this.uploadGeldig()) this.trekIn();
        this.werkBedieningBij();
      }

      trekIn() {
        this.upload = null;
        this.propertiesContainer.innerHTML = '';
        this.werkBedieningBij();
      }

      /* Controles vóór het uploaden (synchroon): { tekst, focus } of null */
      controleer() {
        if (this.laadFout) return { tekst: this.text('load_error'), focus: this.kiesKnop };
        const variant = this.variantMelding();
        if (variant) return { tekst: variant, focus: this.kleurInputs.find((input) => input.checked) };
        if (!this.bron) return { tekst: this.text('error_no_photo'), focus: this.kiesKnop };
        if (!this.klaar) return { tekst: this.text('error_still_loading') };
        if (this.teKlein()) return { tekst: this.text('quality_te_klein'), focus: this.kiesKnop };
        if (!this.toestemmingInput.checked) return { tekst: this.text('error_consent'), focus: this.toestemmingInput };
        if (!this.voorwaardenInput.checked) return { tekst: this.text('error_terms'), focus: this.voorwaardenInput };
        if (!echohangerPlat(this.toestemmingTekstEl) || !echohangerPlat(this.voorwaardenTekstEl)) return { tekst: this.text('error_general') };
        return null;
      }

      /* De klik op In winkelwagen zonder geldige upload: controleren, uploaden, dan het formulier opnieuw versturen */
      async bestel() {
        if (this.bezig) return;
        this.hideError();
        this.verbergToegevoegd();
        if (this.uploadGeldig()) {
          this.verstuurFormulier();
          return;
        }
        const probleem = this.controleer();
        if (probleem) {
          this.zetStatus('');
          this.showError(probleem.tekst);
          this.naarProbleem(probleem.focus);
          return;
        }
        const gelukt = await this.uploaden();
        if (gelukt && this.uploadGeldig()) this.verstuurFormulier();
      }

      /* Het formulier opnieuw versturen; nu laat onSubmit het door naar Dawn (die legt het in de winkelwagen) */
      verstuurFormulier() {
        const knop = this.knop();
        if (!this.form || !knop || knop.disabled) return;
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(knop);
        else knop.click();
      }

      /* De echo naar de dienst (schema 3). -> true als de upload gelukt is (properties staan klaar) */
      async uploaden() {
        const B = this.B;
        const sleutel = this.sleutel();
        const bron = this.bron;
        const kleur = this.kleur;
        const variantId = this.variant.id;
        this.zetBezig(true);
        this.zetStatus(this.text('uploading'));
        clearTimeout(this.traagTimer);
        this.traagTimer = setTimeout(() => this.zetStatus(this.text('uploading_slow')), 6000);
        try {
          await echohangerNaSchilderen();
          /* de echo: grijs 8 bit -> PNG via CompressionStream('deflate'); geen pHYs of tekst (alleen pixels) */
          const png = await B.pngGrijs8(bron.w, bron.h, bron.data, {}, echohangerDeflate);
          const [toestemmingHash, voorwaardenHash] = await Promise.all([
            echohangerSha256(echohangerPlat(this.toestemmingTekstEl)),
            echohangerSha256(echohangerPlat(this.voorwaardenTekstEl)),
          ]);
          const tijd = new Date().toISOString();
          const lichaam = {
            schema: 3,
            versies: { beeld: B.VERSIE, configurator: ECHOHANGER_VERSIE },
            ontwerp: { kleur },
            beeld: { breedte: bron.w, hoogte: bron.h },
            toestemming: { gegeven: true, tekstHash: toestemmingHash.slice(0, 16), tijd, taal: this.taal() },
            voorwaarden: { gegeven: true, tekstHash: voorwaardenHash.slice(0, 16), tijd },
            png: echohangerBase64(png),
          };
          if (this.isEditor()) {
            /* Thema-editor: niets uploaden en niets in de winkelwagen, wel laten zien dat de echo te maken is */
            console.info('[echohanger] Proef in de thema-editor (niet geüpload):', { ...lichaam, png: `${png.length} bytes` });
            this.zetStatus(this.text('design_mode_test'), true);
            return false;
          }
          const antwoord = await this.verstuur(lichaam);
          if (!this.isConnected) return false;
          if (sleutel !== this.sleutel()) {
            /* intussen iets gewijzigd: deze upload hoort niet bij de keuzes van nu; de volgende klik uploadt opnieuw */
            this.zetStatus('');
            return false;
          }
          this.upload = { id: antwoord.id, hash: antwoord.hash, tijd, toestemmingHash, voorwaardenHash, variantId, sleutel };
          this.zetProperties();
          this.hideError();
          this.zetStatus('');
          return true;
        } catch (fout) {
          this.zetStatus('');
          this.toonUploadFout(fout);
          return false;
        } finally {
          clearTimeout(this.traagTimer);
          this.zetBezig(false);
        }
      }

      async verstuur(lichaam) {
        if (!this.dienst) throw { soort: 'dienst', status: 0, code: 'geen_dienst' };
        let antwoord;
        try {
          antwoord = await echohangerMetTijd(
            `${this.dienst}/api/upload`,
            {
              method: 'POST',
              mode: 'cors',
              credentials: 'omit',
              cache: 'no-store',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(lichaam),
            },
            90000
          );
        } catch (error) {
          throw { soort: 'netwerk', fout: error };
        }
        let json = null;
        try {
          json = await antwoord.json();
        } catch (error) {
          json = null;
        }
        if (antwoord.status === 201 && json && ECHOHANGER_ID.test(String(json.id)) && ECHOHANGER_HASH.test(String(json.hash))) {
          return { id: String(json.id), hash: String(json.hash).toLowerCase() };
        }
        throw {
          soort: 'dienst',
          status: antwoord.status,
          code: json && typeof json.fout === 'string' ? json.fout : '',
          reden: json && typeof json.reden === 'string' ? json.reden : '',
          retryAfter: antwoord.headers.get('Retry-After'),
        };
      }

      /* Elke foutcode van SPEC 7 een begrijpelijke melding; de code zelf klein eronder (voor de klantenservice) */
      toonUploadFout(fout) {
        let tekst;
        let code = '';
        let opnieuw = false;
        if (fout && fout.soort === 'netwerk') {
          tekst = this.text('upload_error_network');
          code = 'netwerk';
          opnieuw = true;
        } else if (fout && fout.soort === 'dienst') {
          code = fout.code ? `${fout.code}${fout.reden ? ` (${String(fout.reden).slice(0, 40)})` : ''}` : `http ${fout.status}`;
          switch (fout.code) {
            case 'te_veel': {
              const seconden = parseInt(fout.retryAfter, 10);
              tekst =
                seconden > 0
                  ? this.text('upload_error_rate_minutes', { minutes: Math.max(1, Math.ceil(seconden / 60)) })
                  : this.text('upload_error_rate');
              break;
            }
            case 'opslag':
              tekst = this.text('upload_error_busy');
              opnieuw = true;
              break;
            case 'herkomst':
              tekst = this.text('upload_error_origin');
              break;
            case 'versie':
              tekst = this.text('upload_error_version');
              break;
            case 'kleur':
              tekst = this.text('upload_error_color');
              break;
            case 'toestemming':
              tekst = this.text('upload_error_consent');
              break;
            case 'voorwaarden':
              tekst = this.text('upload_error_terms');
              break;
            case 'te_groot':
            case 'beeld_formaat':
            case 'beeld_maat':
              tekst = this.text('upload_error_image');
              break;
            default:
              if (fout.status === 429) {
                tekst = this.text('upload_error_rate');
              } else if (fout.status === 502 || fout.status === 503 || fout.status === 504) {
                tekst = this.text('upload_error_busy');
                opnieuw = true;
              } else {
                tekst = this.text('upload_error_other');
                opnieuw = true;
              }
          }
        } else {
          console.error('[echohanger] Uploaden mislukt', fout);
          tekst = this.text('upload_error_other');
          code = 'browser';
          opnieuw = true;
        }
        this.showError(tekst);
        if (this.foutcodeEl) {
          this.foutcodeEl.textContent = code ? this.text('error_code', { code }) : '';
          this.foutcodeEl.hidden = !code;
        }
        if (this.opnieuwKnop) this.opnieuwKnop.hidden = !opnieuw;
        this.dataset.fout = code;
      }

      /* ---------- properties ---------- */

      zetProperties() {
        this.propertiesContainer.innerHTML = '';
        const u = this.upload;
        if (!u) return;
        const voegToe = (key, value) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${key}]`;
          input.value = value;
          input.setAttribute('form', this.formId);
          this.propertiesContainer.appendChild(input);
        };
        /* Alleen verborgen properties: de kleur staat al in de varianttitel */
        voegToe('_EchoId', u.id);
        voegToe('_Ontwerp', `${this.dienst}/p/${u.id}`);
        voegToe('_EchoHash', u.hash.slice(0, 16));
        voegToe('_Toestemming echo', `ja · ${u.tijd} · tekst ${u.toestemmingHash.slice(0, 8)}`);
        voegToe('_Voorwaarden', `ja · ${u.tijd} · tekst ${u.voorwaardenHash.slice(0, 8)}`);
      }

      zetBezig(aan) {
        this.bezig = aan;
        this.classList.toggle('is-bezig', aan);
        this.werkBedieningBij();
        /* Wegklikken tijdens het uploaden: de browser vraagt eerst of de klant de pagina echt wil verlaten
           (niet in de thema-editor). Het vak met de voortgang in beeld houden. */
        if (aan && !this.isEditor()) window.addEventListener('beforeunload', this.onVerlaten);
        else window.removeEventListener('beforeunload', this.onVerlaten);
        if (aan) {
          /* het vak groeit boven de knop: eerst het vak, dan de knop in beeld (op een telefoon zakt de knop anders
             onder de rand); na de statustekst, dus in het volgende frame */
          requestAnimationFrame(() => {
            if (!this.bezig) return;
            this.bestellenSectie?.scrollIntoView({ block: 'nearest' });
            this.knop()?.scrollIntoView({ block: 'nearest' });
          });
        }
      }

      onVerlaten(event) {
        if (!this.bezig) return;
        event.preventDefault();
        event.returnValue = '';
      }

      werkBedieningBij() {
        [this.kiesKnop, this.toestemmingInput, this.voorwaardenInput].forEach((el) => {
          if (el) el.disabled = this.bezig;
        });
        this.kleurInputs.forEach((input) => {
          const zonder = !this.varianten.some((v) => v.kleur === input.value);
          input.disabled = this.bezig || zonder;
          input.closest('.echohanger__kleur')?.classList.toggle('is-zonder-variant', zonder);
        });
        if (this.miniatuur) this.miniatuur.hidden = !this.bron;
        const geldig = this.uploadGeldig();
        this.classList.toggle('is-geupload', geldig);
        this.dataset.staat = this.bezig ? 'bezig' : geldig ? 'geupload' : this.klaar ? 'klaar' : 'laden';
        this.werkWinkelknopBij();
      }

      /* ---------- meldingen ---------- */

      zetStatus(tekst, goed = false) {
        if (!this.statusEl) return;
        this.statusEl.textContent = tekst || '';
        this.statusEl.classList.toggle('is-goed', Boolean(tekst) && goed);
      }

      showError(message, viaFormulier = false) {
        if (this.errorElement) {
          this.errorElement.textContent = message;
          this.errorElement.hidden = false;
        }
        if (this.foutcodeEl) this.foutcodeEl.hidden = true;
        if (this.opnieuwKnop) this.opnieuwKnop.hidden = true;
        if (viaFormulier && this.productForm && typeof this.productForm.handleErrorMessage === 'function') {
          this.productForm.handleErrorMessage(message);
          this.productFormErrorShown = true;
        }
      }

      hideError() {
        if (this.errorElement) {
          this.errorElement.hidden = true;
          this.errorElement.textContent = '';
        }
        if (this.foutcodeEl) this.foutcodeEl.hidden = true;
        if (this.opnieuwKnop) this.opnieuwKnop.hidden = true;
        delete this.dataset.fout;
        if (this.productFormErrorShown && this.productForm && typeof this.productForm.handleErrorMessage === 'function') {
          this.productForm.handleErrorMessage();
          this.productFormErrorShown = false;
        }
      }

      toonBestandFout(tekst) {
        if (!this.bestandFout) return;
        this.bestandFout.textContent = tekst;
        this.bestandFout.hidden = false;
      }

      verbergBestandFout() {
        if (!this.bestandFout) return;
        this.bestandFout.hidden = true;
        this.bestandFout.textContent = '';
      }

      verbergToegevoegd() {
        if (this.toegevoegdEl && !this.toegevoegdEl.hidden) this.toegevoegdEl.hidden = true;
      }

      /* Naar wat er ontbreekt: in beeld schuiven en de focus erop (zonder focus: naar de meldingen) */
      naarProbleem(focus) {
        const glad = !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
        const doel = focus && !focus.disabled ? focus : this.bestellenSectie;
        if (!doel) return;
        const zichtbaar = doel.closest('.echohanger__stap, .echohanger__bestellen') || doel;
        zichtbaar.scrollIntoView({ behavior: glad ? 'smooth' : 'auto', block: 'center' });
        doel.focus({ preventScroll: true });
      }

      /* ---------- bestellen ---------- */

      /* Mag dit verzenden door naar Dawn? Alleen met een geldige upload voor precies deze keuzes */
      klaarVoorWinkelwagen() {
        if (this.bezig || this.variantMelding() || !this.uploadGeldig()) return false;
        if (!this.propertiesContainer.querySelector('input[name="properties[_EchoId]"]')) return false;
        /* De variant hoort bij deze upload (niemand anders hoort hem te wijzigen; zo niet: terugzetten) */
        const idInput = this.form ? this.form.querySelector('input[name="id"]') : null;
        if (idInput && String(idInput.value) !== String(this.upload.variantId)) this.setVariantId(this.upload.variantId);
        return true;
      }

      onSubmit(event) {
        if (this.form && event.target !== this.form) return;
        if (this.klaarVoorWinkelwagen()) {
          this.hideError();
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.bezig) return;
        this.bestel();
      }

      onPaymentButtonClick(event) {
        if (!event.target.closest || !event.target.closest('.shopify-payment-button')) return;
        if (this.klaarVoorWinkelwagen()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const probleem = this.controleer();
        this.showError(probleem ? probleem.tekst : this.text('error_use_cart'), true);
        this.naarProbleem(probleem ? probleem.focus : this.knop());
      }

      /* Na toevoegen (Dawn pubsub): echo en keuzes blijven staan; deze upload hoort bij de bestelregel, een volgende
         ketting is een nieuwe upload */
      onCartUpdate(event) {
        if (!event || event.source !== 'product-form') return;
        const variantInput = this.form ? this.form.querySelector('input[name="id"]') : null;
        if (variantInput && event.productVariantId != null && String(event.productVariantId) !== String(variantInput.value)) return;
        if (!this.upload) return;
        this.trekIn();
        this.hideError();
        this.zetStatus('');
        if (this.toegevoegdEl) {
          this.toegevoegdEl.textContent = this.text('added');
          this.toegevoegdEl.hidden = false;
        }
      }
    }
  );
}
